import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button, 
  Divider, 
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AuthDebugger from './AuthDebugger';

const ProfileTroubleshooting: React.FC = () => {
  const { currentUser, userProfile, isLoading } = useAuth();
  const [manualFetchResult, setManualFetchResult] = useState<any>(null);
  const [manualFetchLoading, setManualFetchLoading] = useState(false);
  const [manualFetchError, setManualFetchError] = useState<string | null>(null);
  
  const handleManualFetch = async () => {
    if (!currentUser) {
      setManualFetchError('No authenticated user to fetch profile for');
      return;
    }
    
    setManualFetchLoading(true);
    setManualFetchError(null);
    
    try {
      // Try getting the profile through our service function
      const serviceFetchResult = await getUserProfile(currentUser.uid);
      
      // Try getting the profile directly from Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      setManualFetchResult({
        throughService: serviceFetchResult,
        directFirestore: {
          exists: userDocSnap.exists(),
          data: userDocSnap.exists() ? userDocSnap.data() : null
        }
      });
    } catch (error: any) {
      setManualFetchError(error.message || 'An error occurred during manual fetch');
    } finally {
      setManualFetchLoading(false);
    }
  };
  
  const possibleIssues = [
    {
      issue: 'Authentication not properly initialized',
      symptoms: 'Authentication shows as loading indefinitely, or currentUser is null despite login',
      solution: 'Check Firebase auth initialization and any custom auth providers'
    },
    {
      issue: 'User profile not stored in Firestore',
      symptoms: 'User can authenticate but profile data is missing',
      solution: 'Verify that user document exists in the "users" collection with the current user\'s UID'
    },
    {
      issue: 'Firestore permissions',
      symptoms: 'Authentication works but profile fetch fails with permission errors',
      solution: 'Check Firestore rules to ensure authenticated users can read their own profile'
    },
    {
      issue: 'Profile data structure mismatch',
      symptoms: 'Data exists in Firestore but isn\'t properly loaded into the app',
      solution: 'Verify the structure of the user document matches expected UserProfile interface'
    }
  ];

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>Profile Troubleshooting</Typography>
        <Typography variant="body1" paragraph>
          This page helps diagnose issues with loading user profile information.
        </Typography>
        
        <AuthDebugger />
        
        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom>Manual Profile Fetch</Typography>
          <Typography variant="body2" paragraph>
            Attempt to manually fetch your profile data from Firestore to verify if the issue is with 
            the data retrieval or with how the app is handling the data.
          </Typography>
          
          <Button 
            variant="contained" 
            onClick={handleManualFetch} 
            disabled={manualFetchLoading || !currentUser}
            sx={{ mb: 2 }}
          >
            {manualFetchLoading ? <CircularProgress size={24} /> : 'Fetch Profile Manually'}
          </Button>
          
          {manualFetchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {manualFetchError}
            </Alert>
          )}
          
          {manualFetchResult && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1">Service Fetch Result:</Typography>
              <Box component="pre" sx={{ 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '200px',
                fontSize: '0.8rem'
              }}>
                {JSON.stringify(manualFetchResult.throughService, null, 2)}
              </Box>
              
              <Typography variant="subtitle1" sx={{ mt: 2 }}>Direct Firestore Result:</Typography>
              <Box component="pre" sx={{ 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '200px',
                fontSize: '0.8rem'
              }}>
                {JSON.stringify(manualFetchResult.directFirestore, null, 2)}
              </Box>
            </Box>
          )}
        </Paper>
        
        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom>Common Issues & Solutions</Typography>
          
          {possibleIssues.map((item, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">{item.issue}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Symptoms:</Typography>
                  <Typography paragraph>{item.symptoms}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Solution:</Typography>
                  <Typography>{item.solution}</Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
        
        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom>Profile Loading Process</Typography>
          <Typography variant="body2" paragraph>
            Understanding the sequence of events that should occur when loading a profile:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Firebase Authentication" 
                secondary="User logs in and Firebase auth state listener detects the authenticated user" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. User UID Retrieved" 
                secondary="The currentUser object provides the UID needed to fetch profile data" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Firestore Profile Fetch" 
                secondary="App queries Firestore for the user document using the UID" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Profile State Updated" 
                secondary="The userProfile state is updated with the retrieved data" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="5. Loading State Completes" 
                secondary="isLoading is set to false and UI shows the profile" 
              />
            </ListItem>
          </List>
          
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => window.location.reload()}
              sx={{ mr: 1 }}
            >
              Refresh Page
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={() => window.localStorage.clear()}
            >
              Clear Local Storage
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProfileTroubleshooting; 